import { useEffect, useState } from "react";

import {
  Center,
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  TableViews,
} from "@/components";
import { JobTrackerStats } from "@/components/job-tracker/JobTrackerStats";
import { JobTrackerTable } from "@/components/job-tracker/JobTrackerTable";
import { JobTrackerTableQueryOptions } from "@/components/job-tracker/JobTrackerTableQueryOptions";
import { JobUpdateModal } from "@/components/job-tracker/JobUpdateModal";

import { useDefaultViewGuard } from "@/hooks/useDefaultViewGuard";
import { useSetTitle } from "@/hooks/useSetTitle";
import { useUpdateView } from "@/hooks/mutations/useUpdateView";
import { useGetJobs } from "@/hooks/queries/useGetJobs";
import { useGetViews } from "@/hooks/queries/useGetViews";
import { useSetView } from "@/hooks/sets/useSetView";
import { defaultJobQueryParams } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { mutations } from "@/lib/mutations";
import {
  JobQueryParams,
  JobTrackerResponse,
  View,
  ViewTableName,
} from "@/types";

const viewQueryParams = {
  tableName: ViewTableName.JOB_TRACKER,
};

export const JobTrackerPage = () => {
  /* useStates */
  const [isJobUpdateModalOpen, setIsJobUpdateModalOpen] = useState(false);
  // If groupBy is set, we want to limit the navigation between jobs in the update modal to be within the same group.
  // The reason for this is simplicity in handling the navigation logic with group by
  const [jobsForUpdateModal, setJobsForUpdateModal] = useState<
    JobTrackerResponse[]
  >([]);
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const [selectedJobIndex, setSelectedJobIndex] = useState<number | null>(null);

  /* Database Queries and Cache Updates */
  // Reads
  const {
    data: jobs,
    isPending: isJobsPending,
    isError: isJobsError,
  } = useGetJobs(selectedView?.queryParams as JobQueryParams);
  const {
    data: views,
    isPending: isViewsPending,
    isError: isViewsError,
  } = useGetViews({
    tableName: ViewTableName.JOB_TRACKER,
  });

  // Updates
  const { mutate: updateView } = useUpdateView();
  const setView = useSetView();

  useSetTitle({
    title: "Job Tracker",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.JOB_TRACKER);
  }, []);

  // Ensure a user has a view for this page
  useDefaultViewGuard({
    views,
    selectedView,
    setSelectedView,
    tableName: ViewTableName.JOB_TRACKER,
    defaultQueryParams: defaultJobQueryParams,
  });

  const handleUpdateView = (queryParams: JobQueryParams) => {
    if (!selectedView) return;

    mutations.updateView({
      setSelectedView: setSelectedView,
      setView: setView,
      updateView: updateView,
      updateViewRequest: { queryParams },
      viewId: selectedView.id,
      viewQueryParams: viewQueryParams,
    });
  };

  const render = () => {
    if (isViewsError || isJobsError) {
      return (
        <Center className="h-full">
          <ErrorText
            errorMessage={
              isViewsError
                ? "An error occurred while loading views. Please let the engineers know if they are not already on it!"
                : "An error occurred while loading jobs. Please let the engineers know if they are not already on it!"
            }
          />
        </Center>
      );
    }

    // !selectedView is redundant (isJobsPending covers it) but needed for TypeScript narrowing
    if (isViewsPending || isJobsPending || !selectedView) {
      return (
        <Center className="h-full">
          <LoadingIndicator variant="xlarge" />
        </Center>
      );
    }

    const queryParams: JobQueryParams = {
      ...defaultJobQueryParams,
      ...(selectedView.queryParams as JobQueryParams),
    };
    const isGrouped = !!queryParams.groupBy;

    return (
      <Column
        className={
          isGrouped ? "gap-4 p-6" : "h-screen gap-4 overflow-hidden p-6"
        }
      >
        <JobTrackerStats jobs={jobs} />
        <TableViews
          defaultQueryParams={defaultJobQueryParams}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          tableName={ViewTableName.JOB_TRACKER}
          viewQueryParams={viewQueryParams}
          views={views}
        />
        <Row className="items-center justify-between">
          <JobTrackerTableQueryOptions
            handleUpdateView={handleUpdateView}
            jobQueryParams={queryParams}
          />
          {jobs.length > 0 ? (
            <JobUpdateModal
              isJobUpdateModalOpen={isJobUpdateModalOpen}
              jobQueryParams={selectedView.queryParams as JobQueryParams}
              jobs={jobsForUpdateModal}
              selectedIndex={selectedJobIndex}
              setIsJobUpdateModalOpen={setIsJobUpdateModalOpen}
              setJobsForUpdateModal={setJobsForUpdateModal}
              setSelectedIndex={setSelectedJobIndex}
            />
          ) : null}
        </Row>
        <div className="max-h-[calc(100vh-13rem)] overflow-auto">
          <JobTrackerTable
            groupBy={queryParams.groupBy}
            handleUpdateView={handleUpdateView}
            jobQueryParams={queryParams}
            jobs={jobs}
            selectedIndex={selectedJobIndex}
            setIsJobUpdateModalOpen={setIsJobUpdateModalOpen}
            setJobsForUpdateModal={setJobsForUpdateModal}
            setSelectedIndex={setSelectedJobIndex}
          />
        </div>
      </Column>
    );
  };

  return render();
};
